'use client';

import PlaneIcon from '@/components/icons/Flights/PlaneIcon';
import PassengersColor from '@/components/icons/PassengersColor';
import CalendarColor from '@/components/illustratores/CalendarColor';
import Button from '@/components/ui/Button';
import { DatePickerModal } from '@/components/ui/DatePicker';
// Assuming useSubmitHotelSearch hook might need adjustments or a new hook for flights
// import { useSubmitHotelSearch } from '@/hooks/useSubmitHotelSearch';
import { faArrowRightArrowLeft, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useState } from 'react';
import FlightTravelersModal from './FlightTravleresSelecorModal';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { format } from 'date-fns';
import getNextDate from '@/utils/getNextDay';
import SearchInput from '@/components/ui/SearchInput';
import FlightSearchInput from './FlightSearchInput';

// Define the interface for flight travelers data (should match the modal's interface)
interface TravelersData {
    adults: number; // Adults aged 12 and older
    children: number[]; // Children aged 2 through 12 (array stores ages)
    infants: number[]; // Infants aged 0 through 2 (array stores ages)
}

type RadioOptionProps = {
    label: string;
    value: string;
    checked: boolean;
    onChange: (value: string) => void;
};

function RadioOption({ label, value, checked, onChange }: RadioOptionProps) {

    return (
        <label className={`
        ${checked ? 'bg-secondary-50 md:bg-white border-b md:border-b-0 text-secondary-500 border-secondary-500' : ''}
        py-2 cursor-pointer text-sm px-4 flex items-center rounded-t-lg gap-2 transition-all duration-200`}>
            <input
                className='md:inline-block hidden'
                type='radio'
                name='flightType'
                checked={checked}
                onChange={() => onChange(value)}
            />
            <span>{label}</span>
        </label>
    );
}

export default function FlightSearch() {
    const [selectedOption, setSelectedOption] = useState('Round-trip');

    const [selectedFromAirport, setSelectedFromAirport] = useState({
        "airport_name": '',
        "airport_code": "",
    })
    const [selectedToAirport, setSelectedToAirport] = useState({
        "airport_name": '',
        "airport_code": "",
    })

    // Define the state for flight travelers data
    const [travelersData, setTravelersData] = useState<TravelersData>({
        adults: 2,
        children: [],
        infants: [],
    });
    const [showTravelersModal, setShowTravelersModal] = useState(false)

    const [flightDates, setFlightDates] = useState<Date | null>(new Date(getNextDate(0)));
    const handleFlightDateChange = (dates: { startDate: Date | null; endDate: Date | null }) => {
        setFlightDates(dates.startDate);
    };

    const formatFlightDate = (date: Date | null) => {
        if (!date) return 'Select Date';
        return format(date, "MMMM d"); // Or format as needed
    };

    function handleChangeAirports() {
        const from = selectedFromAirport;
        const to = selectedToAirport;

        setSelectedFromAirport(to);
        setSelectedToAirport(from);
    }

    const passModal = useOutsideClick(() => {
        innerWidth > 1024 && setShowTravelersModal(false);
    });
    // You will likely need to calculate the total number of travelers for display
    const totalTravelers = travelersData.adults + travelersData.children.length + travelersData.infants.length;

    return (
        <div className=' text-sm py-1 text-gray-700 col-span-3 items-center gap-2.5'>
            <div className='flex items-center md:justify-start justify-between w-full '>
                <RadioOption
                    label='Round-trip'
                    value='Round-trip'
                    checked={selectedOption === 'Round-trip'}
                    onChange={setSelectedOption}
                />
                <RadioOption
                    label='One-way'
                    value='One-way'
                    checked={selectedOption === 'One-way'}
                    onChange={setSelectedOption}
                />
                <RadioOption
                    label='Multi-city'
                    value='Multi-city'
                    checked={selectedOption === 'Multi-city'}
                    onChange={setSelectedOption}
                />
            </div>
            <div className='grid items-center mt-2  gap-2 md:gap-4 grid-cols-2 md:grid-cols-[repeat(4,_1fr),_auto]'>
                <div className='relative md:col-span-1 col-span-full'>
                    <button
                        onClick={() => handleChangeAirports()}
                        className='absolute bg-secondary-50 z-20
                                         md:left-full md:top-1/2 md:-translate-y-1/2 md:-translate-x-3
                                        right-5 top-full -translate-y-4 md:right-auto
                                         text-secondary-500 size-10 p-2 
                                        rounded-xl border border-secondary-200 '>
                        <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                    </button>
                    <FlightFormBox>
                        <div className='border-b-2 border-main-500'>
                            <PlaneIcon className='w-6' />
                        </div>
                        <div className='flex flex-col text-gray-400 grow'>
                            <span className='text-xs font-normal'>From</span>
                            <FlightSearchInput
                                setSelectedAirport={setSelectedFromAirport}
                                selectedAirport={selectedFromAirport}
                            />
                        </div>
                    </FlightFormBox>
                </div>
                <div className='md:col-span-1 col-span-full'>
                    <FlightFormBox>
                        <div className='border-b-2 border-main-500'>
                            <PlaneIcon className='w-6 rotate-45' />
                        </div>
                        <div className='flex flex-col text-gray-400'>
                            <span className='text-xs font-normal'>To</span>
                            <FlightSearchInput
                                setSelectedAirport={setSelectedToAirport}
                                selectedAirport={selectedToAirport}
                            />
                        </div>
                    </FlightFormBox>
                </div>
                <div className='md:col-span-1 col-span-full'>
                    <FlightFormBox>
                        <CalendarColor className='w-6' />
                        <DatePickerModal
                            singleDate // Assuming single date selection for one-way, will need logic for round-trip
                            values={{ startDate: flightDates, endDate: null }}
                            onChange={handleFlightDateChange}
                            buttonProps={{}} // Add necessary button props
                        >
                            <div className='flex cursor-pointer flex-col text-gray-400'>
                                <span className='text-xs font-normal'>Date</span>
                                <span className='text-gray-500 text-base'>
                                    {formatFlightDate(flightDates)}
                                </span>
                            </div>
                        </DatePickerModal>
                    </FlightFormBox>
                </div>
                <div className='relative' ref={passModal}>
                    <FlightFormBox>
                        <PassengersColor className='w-6' />
                        <div

                            onClick={() => setShowTravelersModal(true)} className='flex flex-col text-gray-400'>
                            <span className='text-xs font-normal'>Travelers</span>
                            {/* Display total travelers */}
                            <span className='text-gray-500 text-base'>
                                {totalTravelers} Traveler{totalTravelers !== 1 ? 's' : ''}
                            </span>

                        </div>

                    </FlightFormBox>
                    <FlightTravelersModal
                        onClose={() => setShowTravelersModal(false)}
                        showModal={showTravelersModal}
                        setTravelersData={setTravelersData}
                        travelersData={travelersData}
                    />
                </div>
                <div className='md:hidden h-full'>
                    <FlightFormBox>
                        <PassengersColor className='w-6' />
                        <div>
                            Economy
                        </div>
                    </FlightFormBox>
                </div>
                <div className='col-span-2 md:col-span-1 flex items-center justify-center'>

                    {selectedOption === 'Multi-city' ?
                        <button className='hidden md:inline-block'>
                            <FontAwesomeIcon icon={faX} />
                        </button>
                        : <Button color='primary' className='h-fit mx-auto' radius='rounded-xl'>
                            Search <span className='md:hidden'>Flights</span>
                        </Button>}
                </div>
            </div>
        </div>
    );
}

function FlightFormBox({ children }: { children: ReactNode }) {
    return <div className='border relative h-full gap-4 border-gray-300 flex items-center rounded-xl px-4 py-3.5'>
        {children}
    </div>
}
